import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { getSocket } from './useRealtimeSync';
import {
  getAgentChatStreamState,
  resetAgentChatStreamState,
  subscribeAgentChatStreamState,
  updateAgentChatStreamState,
} from '@/components/Messenger/cache/agentChatStreamStore';
import { setAgentChatProcessingFlag } from '@/components/Messenger/cache/agentChatsProcessingStore';

export interface LiveToolCall {
  toolCallId: string;
  toolName: string;
  status: 'calling' | 'done';
}

export type LiveStreamPhase = 'submitted' | 'reasoning' | 'tool' | 'text';

export interface LiveStreamState {
  isActive: boolean;
  isDone: boolean;
  isReasoning: boolean;
  phase: LiveStreamPhase | null;
  toolCalls: LiveToolCall[];
  text: string;
}

const MIN_TOOL_CALL_DISPLAY_MS = 1000;

type ChatScopedPayload = { chat_id?: unknown };

function payloadChatId(data: ChatScopedPayload | undefined): string | null {
  if (!data) return null;
  const raw = data.chat_id;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed || null;
}

function markProcessing(chatId: string, active: boolean) {
  setAgentChatProcessingFlag(chatId, active);
}

export function useChatStream(chatId?: string | null) {
  const resolvedId = chatId?.trim() || '';
  const toolStartTimesRef = useRef<Map<string, number>>(new Map());
  const pendingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const state = useSyncExternalStore(
    useCallback(
      (onChange) => (resolvedId ? subscribeAgentChatStreamState(resolvedId, onChange) : () => {}),
      [resolvedId]
    ),
    () => (resolvedId ? getAgentChatStreamState(resolvedId) : getAgentChatStreamState('')),
    () => getAgentChatStreamState('')
  );

  const reset = useCallback((targetChatId?: string | null) => {
    const id = (targetChatId ?? resolvedId).trim();
    if (!id) return;
    resetAgentChatStreamState(id);
    markProcessing(id, false);
    const startPrefix = `${id}::`;
    for (const key of [...toolStartTimesRef.current.keys()]) {
      if (key.startsWith(startPrefix)) toolStartTimesRef.current.delete(key);
    }
    for (const [key, timeoutId] of [...pendingTimeoutsRef.current.entries()]) {
      if (!key.startsWith(startPrefix)) continue;
      clearTimeout(timeoutId);
      pendingTimeoutsRef.current.delete(key);
    }
  }, [resolvedId]);

  useEffect(() => {
    const socket = getSocket();
    const pendingTimeouts = pendingTimeoutsRef.current;
    const toolStartTimes = toolStartTimesRef.current;

    const updateChatState = (
      targetChatId: string,
      updater: (prev: LiveStreamState) => LiveStreamState
    ): void => {
      updateAgentChatStreamState(targetChatId, updater);
    };

    const onReasoning = (data?: ChatScopedPayload) => {
      const targetChatId = payloadChatId(data);
      if (!targetChatId) return;
      markProcessing(targetChatId, true);
      updateChatState(targetChatId, (prev) => {
        if (prev.isDone && !prev.isActive) return prev;
        if (prev.isActive && prev.isReasoning && !prev.isDone) return prev;
        return {
          ...prev,
          isActive: true,
          isDone: false,
          isReasoning: true,
          phase: 'reasoning',
        };
      });
    };

    const onSubmitted = (data?: ChatScopedPayload) => {
      const targetChatId = payloadChatId(data);
      if (!targetChatId) return;
      markProcessing(targetChatId, true);
      updateChatState(targetChatId, (prev) => {
        if (prev.isActive && !prev.isDone && !prev.isReasoning) return prev;
        return {
          ...prev,
          isActive: true,
          isDone: false,
          isReasoning: false,
          phase: 'submitted',
        };
      });
    };

    const onToolStart = (data: { toolCallId: string; toolName: string; chat_id?: unknown }) => {
      const targetChatId = payloadChatId(data);
      if (!targetChatId) return;
      markProcessing(targetChatId, true);
      const key = `${targetChatId}::${data.toolCallId}`;
      toolStartTimes.set(key, Date.now());
      updateChatState(targetChatId, (prev) => {
        if (prev.isDone && !prev.isActive) return prev;
        if (prev.toolCalls.some((tc) => tc.toolCallId === data.toolCallId && tc.status === 'calling')) {
          return prev;
        }
        return {
          ...prev,
          isActive: true,
          isDone: false,
          isReasoning: false,
          phase: 'tool',
          toolCalls: [
            ...prev.toolCalls,
            { toolCallId: data.toolCallId, toolName: data.toolName, status: 'calling' },
          ],
        };
      });
    };

    const applyToolResult = (
      targetChatId: string,
      data: { toolCallId: string; toolName: string }
    ) => {
      updateChatState(targetChatId, (prev) => {
        if (prev.isDone && !prev.isActive) return prev;
        let changed = false;
        const nextToolCalls: LiveToolCall[] = prev.toolCalls.map((tc): LiveToolCall => {
          if (tc.toolCallId !== data.toolCallId) return tc;
          changed = true;
          return { ...tc, status: 'done' as const };
        });
        if (!changed) return prev;
        const hasCalling = nextToolCalls.some((tc) => tc.status === 'calling');
        return {
          ...prev,
          isReasoning: false,
          phase: hasCalling ? 'tool' : 'submitted',
          toolCalls: nextToolCalls,
        };
      });
    };

    const onToolResult = (data: {
      toolCallId: string;
      toolName: string;
      chat_id?: unknown;
    }) => {
      const targetChatId = payloadChatId(data);
      if (!targetChatId) return;
      const key = `${targetChatId}::${data.toolCallId}`;
      const startedAt = toolStartTimes.get(key);
      toolStartTimes.delete(key);
      const elapsed = startedAt != null ? Date.now() - startedAt : MIN_TOOL_CALL_DISPLAY_MS;
      const delay = Math.max(0, MIN_TOOL_CALL_DISPLAY_MS - elapsed);
      if (delay > 0) {
        const id = setTimeout(() => {
          pendingTimeouts.delete(key);
          applyToolResult(targetChatId, data);
        }, delay);
        pendingTimeouts.set(key, id);
      } else {
        applyToolResult(targetChatId, data);
      }
    };

    const onTextDelta = (data: { delta: string; chat_id?: unknown }) => {
      const targetChatId = payloadChatId(data);
      if (!targetChatId) return;
      markProcessing(targetChatId, true);
      updateChatState(targetChatId, (prev) => {
        if (prev.isDone && !prev.isActive) return prev;
        if (prev.isActive && !prev.isReasoning && prev.text) return prev;
        return {
          ...prev,
          isActive: true,
          isDone: false,
          isReasoning: false,
          phase: 'text',
          text: prev.text || '1',
        };
      });
    };

    const onDone = (data?: ChatScopedPayload) => {
      const targetChatId = payloadChatId(data);
      if (!targetChatId) return;
      markProcessing(targetChatId, false);
      const timeoutPrefix = `${targetChatId}::`;
      for (const [key, timeoutId] of [...pendingTimeouts.entries()]) {
        if (!key.startsWith(timeoutPrefix)) continue;
        clearTimeout(timeoutId);
        pendingTimeouts.delete(key);
      }
      for (const key of [...toolStartTimes.keys()]) {
        if (key.startsWith(timeoutPrefix)) toolStartTimes.delete(key);
      }
      updateChatState(targetChatId, (prev) => {
        if (prev.isDone && !prev.isActive) return prev;
        return {
          ...prev,
          isActive: false,
          isDone: true,
          isReasoning: false,
          phase: null,
        };
      });
    };

    socket.on('chat:submitted', onSubmitted);
    socket.on('chat:reasoning', onReasoning);
    socket.on('chat:tool_start', onToolStart);
    socket.on('chat:tool_result', onToolResult);
    socket.on('chat:text_delta', onTextDelta);
    socket.on('chat:done', onDone);

    return () => {
      socket.off('chat:submitted', onSubmitted);
      socket.off('chat:reasoning', onReasoning);
      socket.off('chat:tool_start', onToolStart);
      socket.off('chat:tool_result', onToolResult);
      socket.off('chat:text_delta', onTextDelta);
      socket.off('chat:done', onDone);
      pendingTimeouts.forEach((id) => clearTimeout(id));
      pendingTimeouts.clear();
      toolStartTimes.clear();
    };
  }, []);

  return { state, reset };
}
